// Utility to prevent Supabase from pausing due to inactivity
import supabase from './supabase';

// Keep track of when we last pinged Supabase
let lastPingTime = null;
let pingCount = 0;

// Ping interval in milliseconds (2 days = 172800000 ms)
// We use 2 days to ensure we're well within the 7-day inactivity limit
const PING_INTERVAL = 2 * 24 * 60 * 60 * 1000; 
const RETRY_INTERVAL = 30 * 60 * 1000; // 30 minutes for retries

/**
 * Simple query to keep the Supabase connection alive
 * This function is designed to be very lightweight
 */
export const pingSupabase = async () => {
  try {
    console.log(`🏓 Pinging Supabase... (Attempt #${pingCount + 1})`);
    
    // Try multiple methods in order of preference
    let success = false;
    let error = null;

    // Method 1: Try RPC function first
    try {
      const { data, error: rpcError } = await supabase.rpc('keepalive_ping');
      if (!rpcError) {
        success = true;
        console.log('✅ RPC ping successful');
      } else {
        error = rpcError;
      }
    } catch (e) {
      error = e;
    }

    // Method 2: If RPC fails, try simple query to ping_log
    if (!success) {
      try {
        const { error: queryError } = await supabase
          .from('ping_log')
          .select('count')
          .limit(1);
        
        if (!queryError) {
          success = true;
          console.log('✅ Query ping successful');
        } else {
          error = queryError;
        }
      } catch (e) {
        error = e;
      }
    }

    // Method 3: Last resort - just get session
    if (!success) {
      try {
        const { error: sessionError } = await supabase.auth.getSession();
        if (!sessionError) {
          success = true;
          console.log('✅ Session ping successful');
        } else {
          error = sessionError;
        }
      } catch (e) {
        error = e;
      }
    }

    if (success) {
      pingCount++;
      lastPingTime = new Date();
      console.log(`✅ Supabase ping successful at ${lastPingTime.toISOString()} (Total pings: ${pingCount})`);
      return true;
    } else {
      console.error('❌ All ping methods failed:', error);
      return false;
    }
  } catch (err) {
    console.error('❌ Supabase ping failed:', err);
    return false;
  }
};

/**
 * Initialize the keepalive mechanism
 * This should be called when the app starts
 */
export const initKeepAlive = () => {
  console.log('🔄 Initializing Supabase keepalive mechanism...');
  
  // Ping immediately on startup
  pingSupabase();
  
  // Set up the interval to ping every PING_INTERVAL
  const intervalId = setInterval(pingSupabase, PING_INTERVAL);
  
  // Set up retry interval for failed pings
  const retryId = setInterval(async () => {
    const now = new Date();
    if (!lastPingTime || (now - lastPingTime) > RETRY_INTERVAL) {
      console.log('🔄 Retrying ping after failure...');
      await pingSupabase();
    }
  }, RETRY_INTERVAL);
  
  // Also ping when the user becomes active after being away
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const now = new Date();
      // Only ping if we haven't pinged recently (in the last 6 hours)
      if (!lastPingTime || now - lastPingTime > 6 * 60 * 60 * 1000) {
        console.log('👁️ User became active, pinging Supabase...');
        pingSupabase();
      }
    }
  });
  
  // Listen for network reconnection events
  window.addEventListener('online', () => {
    console.log('🌐 Network connection restored, pinging Supabase...');
    pingSupabase();
  });

  // Add periodic status logging
  setInterval(() => {
    if (lastPingTime) {
      const hoursSinceLastPing = (new Date() - lastPingTime) / (1000 * 60 * 60);
      console.log(`📊 Status: Last ping was ${hoursSinceLastPing.toFixed(1)} hours ago. Total pings: ${pingCount}`);
    }
  }, 60 * 60 * 1000); // Log every hour
  
  return () => {
    // Return cleanup function to clear intervals if needed
    clearInterval(intervalId);
    clearInterval(retryId);
  };
};

export default initKeepAlive;